<script setup>
/* eslint semi: 1 */
import { FwbButton, FwbDropdown, FwbRadio } from 'flowbite-vue';
import { useMediaSorting } from '@/composables/media/use-media-sorting';
import { onMounted } from 'vue';

const {
  schema,
  isNonDefault,
  setGroupValue,
  initialize,
  apply,
  reset
} = useMediaSorting();

const emit = defineEmits(['apply', 'clear']);

function onApply () {
  apply();
  emit('apply');
  closeDropdown();
}

function onReset () {
  reset();
  emit('clear');
  closeDropdown();
}

function closeDropdown () {
  /**
   * Simulates a click outside the dropdown to trigger its
   * close behavior. This is needed because the dropdown
   * component does not provide a way to close it and uses
   * a click outside listener.
   */
  setTimeout(() => {
    document.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  });
}

onMounted(() => {
  // Initialize the schema on mount to reflect the current query state
  initialize();
});
</script>
<template>
  <FwbDropdown
    ref="dropdownRef"
    placement="bottom"
    class="cursor-pointer group z-[11]"
    content-wrapper-class="media-sort-content bottom-[unset] top-12 h-fit bg-white shadow-2xl rounded border-gray-200 border"
    :close-inside="false"
    data-component-element-id="media-sort-dropdown"
    @show="initialize"
  >
    <template #trigger>
      <FwbButton
        class="justify-center px-2 font-medium"
        data-component-element-id="media-sort-actions-btn"
        color="light"
      >
        <template #prefix>
          <BaseIconSvg
            name="flowbite:sort-outline"
            class="w-4! h-4!"
          />
        </template>

        <span>{{ $t('label.sort') }}</span>

        <template #suffix>
          <BaseIconSvg
            name="flowbite:angle-down-outline"
            class="w-4! h-4! group-[&.fwb-dropdown-active]:-rotate-180 transition-transform duration-200 text-gray-900!"
          />
        </template>
      </FwbButton>
    </template>

    <div class="w-72 flex flex-col p-3 gap-3">
      <!-- Panel header -->
      <div class="flex items-center justify-between text-sm font-medium">
        <span class="text-gray-900">{{ $t('label.sortBy') }}</span>
        <button
          v-if="isNonDefault"
          type="button"
          class="h-7 flex items-center bg-transparent hover:bg-transparent border-0! text-primary-700 hover:text-primary-900"
          data-component-element-id="media-sort-reset-btn"
          :aria-label="$t('buttons.reset')"
          @click="onReset"
        >
          {{ $t('buttons.reset') }}
        </button>
      </div>

      <!-- Options -->
      <div class="flex flex-col">
        <div
          v-for="group in schema"
          :key="group.key"
          :data-component-element-id="`media-sort-group-${group.key}`"
          class="flex flex-col py-1"
        >
          <FwbRadio
            v-for="option in group.options"
            :key="option.value"
            class="cursor-pointer [&>input]:focus:ring-transparent! text-start"
            :model-value="group.value"
            :value="option.value"
            :label="$t(option.i18n)"
            :data-component-element-id="`media-sort-group-${group.key}-option-${option.value}`"
            @update:model-value="setGroupValue(group.key, option.value)"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end">
        <FwbButton
          class="justify-center border-none"
          size="sm"
          color="blue"
          data-component-element-id="media-sort-apply-btn"
          @click="onApply"
        >
          {{ $t('buttons.apply') }}
        </FwbButton>
      </div>
    </div>
  </FwbDropdown>
</template>
